import { useCallback } from 'react';
import { LinkTypeEnum } from '@hqo/hqo-miniapp-client-sdk';
import { NON_RECORD_ACTIONS } from 'app/shared/consts/activity';
import { useRememberSelection } from 'app/shared/hooks/use-selection-memory';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useBuilding } from 'app/shared/contexts/building';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { usePortalMfeButton } from 'app/shared/hooks/use-portal-mfe-button';
import type { CreateTarget } from 'app/shared/hooks/use-building-verticals';
import { HOST_PLATFORMS, MODULE_COMMAND_VALUE } from 'app/shared/consts';
import { useSdkHostInfo } from 'app/shared/contexts/host-info';
import { ActivityAction, ActivityItem, ActivityType } from 'app/data/types/activity';
import {
  buildActivityDeeplink,
  buildBaseDeeplink,
  ENV_URLS,
  getPrimaryAction,
  getRecordAction,
  isOurOrigin,
} from 'app/shared/utils/build-activity-deeplink';

/**
 * The host pages this app asks for by name. Matches `STABLE_NATIVE_ROUTE_BY_VALUE` in tenant-portal,
 * which is the closed list of routes it will resolve — a name absent from there does nothing.
 */
export type HostPage = 'HOME' | 'EVENTS';

/**
 * What the caller knows about the building's button for a module that this hook does not fetch.
 *
 * Passed in rather than looked up here: the feed already holds the building's buttons, and the other
 * caller (the booking detail page) does not, nor need them — fetching them here would add a request to
 * a page that discards it.
 */
export interface ModuleOptions {
  /** Where the building's own button for the vertical goes (VIS-2189). Absent = our create flow. */
  target?: CreateTarget;
}

/**
 * The host command type for following a button's url the way the button itself opens it. A
 * microfrontend button is a url in a webview, the same as a module url always was.
 */
const LINK_TYPE_BY_BUTTON_TYPE: Record<string, LinkTypeEnum> = {
  WEBVIEW: LinkTypeEnum.WEBVIEW,
  MICROFRONTEND: LinkTypeEnum.WEBVIEW,
  DEEPLINK: LinkTypeEnum.EXTERNAL_DEEPLINK,
  NATIVELINK: LinkTypeEnum.NATIVE_LINK,
};

interface UseActivityDeeplink {
  /** Resolve the webview url for an item/action, or `null` if it can't be routed. */
  getDeeplinkUrl: (item: ActivityItem, action?: ActivityAction) => string | null;
  /** Whether the item has at least one routable action (drives card tappability). */
  canOpen: (item: ActivityItem) => boolean;
  /** Open the item's action as a webview via the SDK, with a host `window.open` fallback. */
  openActivityItem: (item: ActivityItem, action?: ActivityAction) => void;
  /** Whether a type's base miniapp is routable (drives empty-state CTA visibility). */
  canOpenModule: (type: ActivityType, options?: ModuleOptions) => boolean;
  /** The url `openModule` would open, for callers that need the destination without navigating. */
  getModuleUrl: (type: ActivityType, options?: ModuleOptions) => string | null;
  /** Open a type's base miniapp landing page (empty-state CTA). */
  openModule: (type: ActivityType, options?: ModuleOptions) => void;
  /**
   * Whether the host can be asked for one of its own pages by name. Only the tenant portal can:
   * it maps a uuid-less `NATIVE_SCREEN` onto its stable routes. Callers gate their affordance on
   * this rather than offering a button that would do nothing.
   */
  canOpenHostPage: boolean;
  /** Ask the host to navigate to one of its own stable pages. */
  openHostPage: (page: HostPage) => void;
  /**
   * Record that the reader is on this item's IN-APP screen, so a host pane can pick it up later.
   *
   * The popover's counterpart to what `openActivityItem` stores on the split surface. Nothing is
   * sent to the host — the reader stays here — this only leaves behind the command the split
   * surface WOULD have sent, so expanding to full screen arrives on the same record.
   */
  rememberInAppRecord: (item: ActivityItem) => void;
}

/**
 * The `redirectToContent` payload for a url.
 *
 * Extracted because it is now built in two places: once to SEND to the host, and once to STORE
 * without sending, when the reader opens a record in-app on the popover. Those two must produce
 * the same object — a stored command is replayed by the host verbatim, so a shape that drifted
 * from the sent one would fail only later, on the surface that restores it.
 */
const buildWebviewCommand = (
  url: string,
  {
    utilityButtonUuid, targetBuildingUuid, isModule = false, linkType = LinkTypeEnum.WEBVIEW,
  }: Omit<OpenUrlOptions, 'recordId'> = {},
) => ({
  // Says which KIND of thing this is, so a host with a record pane can keep modules on
  // their own route. Unread by every host that does not have one.
  value: isModule ? MODULE_COMMAND_VALUE : '',
  type: linkType,
  options: {
    url,
    ...(utilityButtonUuid ? { uuid: utilityButtonUuid } : {}),
    // The building the RECORD belongs to — a HINT, not an instruction. The portal switches to it
    // when the reader can be routed there and stays put when they cannot: a booking names the
    // RESOURCE's building, which a tenant can hold a booking in without the portal listing it, and
    // only the portal has the list to tell those apart. Named `building_uuid` because that is the
    // key the portal's redirect handler reads, the same one the notification centre sends.
    ...(targetBuildingUuid ? { building_uuid: targetBuildingUuid } : {}),
  },
});

/** What `openUrl` needs beyond the url itself. An object because the tail grew past reading well. */
interface OpenUrlOptions {
  utilityButtonUuid?: string;
  targetBuildingUuid?: string;
  /** A page rather than a record: it belongs on its own route and has no pane to be restored into. */
  isModule?: boolean;
  /** The activity item being opened, when there is one. Present only for records. */
  recordId?: string;
  /** How the host should open the url. A webview unless following a button that opens otherwise. */
  linkType?: LinkTypeEnum;
}

export const useActivityDeeplink = (): UseActivityDeeplink => {
  const sdk = useMiniappSdk();
  const portalMfeButtonUuid = usePortalMfeButton();
  const { hostPlatform } = useSdkHostInfo();
  const isTenantPortal = hostPlatform === HOST_PLATFORMS.TENANT_PORTAL;
  const { building } = useBuilding();
  const { authToken, apiUrl, appBrand, locale, buildingUuid: paramsBuildingUuid, hostSurface } = useOpenParams();
  const buildingUuid = building?.uuid ?? paramsBuildingUuid;
  // The building the HOST is on, which is what scopes a remembered selection.
  const rememberSelection = useRememberSelection(buildingUuid, hostSurface === 'fullscreen');
  /*
   * The popover's own writer, for the one tap there that lands on a record and stays: the in-app
   * booking screen. Separate from the one above rather than a widened gate, because the popover
   * must NOT remember its other taps — those leave for another miniapp entirely.
   */
  const rememberInAppSelection = useRememberSelection(buildingUuid, hostSurface === 'popover');
  /*
   * The portal's full-page surface, which shows the record in a COLUMN beside this list. It is the
   * one surface where a record's destination differs, because a column is not a page — see
   * `buildInPane` in the route map.
   */
  const isSplitSurface = hostSurface === 'fullscreen';

  /**
   * The building the RECORD belongs to, which is not necessarily the one the host is showing — the
   * feed is cross-building. Every url below is built against it, so a visit in another building
   * opens THAT building's visits rather than filtering the current building's by the visit's date.
   * Falls back to the host's building for an item whose projection carries no context.
   */
  const buildingUuidFor = useCallback(
    (item: Pick<ActivityItem, 'context'>): string | undefined => item.context?.buildingUuid ?? buildingUuid,
    [buildingUuid],
  );

  const getDeeplinkUrl = useCallback(
    (item: ActivityItem, action?: ActivityAction): string | null => {
      const itemBuildingUuid = buildingUuidFor(item);
      /*
       * An item must carry an action to be openable (display-only items have none).
       *
       * ⚠️ THE RECORD ACTION FIRST when the caller named none. A bare tile tap means "open this
       * thing", and the action that opens it is `open_record` wherever it sits in the list -- not
       * whichever action happens to be at index 0.
       *
       * Today the two agree: search-service appends `book_again` AFTER `open_record` and gates it on
       * the same projected field the routing details come from, so a booking cannot offer the
       * creation flow first. But `rememberInAppRecord` below already reads the RECORD action for
       * exactly this reason, and two readings of "which action is in play" that agree only because
       * of an upstream ordering neither of them enforces is a disagreement waiting to happen -- the
       * tap would route by one action and the pane remember the other.
       *
       * `getPrimaryAction` stays the fallback, for the types that offer no `open_record` at all.
       */
      const resolved = action ?? getRecordAction(item) ?? getPrimaryAction(item);
      if (!resolved) return null;
      const paneOption = { inPane: isSplitSurface };

      // Visitors open the record or nothing — a visits landing page is not a useful destination.
      if (item.type === 'visitor_registration') {
        return buildActivityDeeplink(item, resolved, itemBuildingUuid, ENV_URLS, paneOption);
      }
      // Falls back to the landing if the id is missing — better than a dead tile.
      if (item.type === 'work_order' || item.type === 'event') {
        return buildActivityDeeplink(item, resolved, itemBuildingUuid, ENV_URLS, paneOption)
          ?? buildBaseDeeplink(item, ENV_URLS, itemBuildingUuid);
      }

      /*
       * A booking opens in the resource-booking app, wherever the reader is — its own bookings page
       * on a surface that gives a record a page, the single booking in a column. There is no longer
       * an in-app screen to fall back on: a booking now routes out the way an event always has.
       *
       * Gated on the transaction rather than handed to `buildActivityDeeplink` outright: with only
       * a resource the config answers `resources/:uuid`, and the resource-booking app bounces that
       * page to its landing when it is the first history entry and the reader has any bookings
       * (`use-redirect-user-to-landing`, unless `skipRedirect=true`). The module home is the
       * honest destination for a document with no transaction, and is what those documents get
       * today.
       */
      if (item.type === 'resource_booking' && resolved.routingDetails?.transactionUuid) {
        return buildActivityDeeplink(item, resolved, itemBuildingUuid, ENV_URLS, paneOption)
          ?? buildBaseDeeplink(item, ENV_URLS, itemBuildingUuid);
      }

      return buildBaseDeeplink(item, ENV_URLS, itemBuildingUuid);
    },
    [buildingUuidFor, isSplitSurface],
  );

  const canOpen = useCallback(
    (item: ActivityItem): boolean => getDeeplinkUrl(item) !== null,
    [getDeeplinkUrl],
  );

  // Embedded in the tenant portal → hand the webview to the SDK host. Standalone (no host — a dev or
  // preview tab, where `redirectToContent` exists but silently no-ops) → open the target in a new tab so
  // the action isn't a dead click.
  //
  // `utilityButtonUuid` names the vertical this url belongs to. The tenant portal reads it and routes
  // to that vertical's own MFE instead of framing us; every other host ignores it and opens the url, so
  // the url stays the contract and the uuid is only ever an upgrade. Omitted for anything that must
  // land on a specific RECORD — the portal's MFE routes reach a vertical, not a booking or a visit, so
  // naming a button there would trade an exact destination for a list.
  const openUrl = useCallback(
    (
      url: string,
      {
        utilityButtonUuid, targetBuildingUuid, isModule = false, recordId, linkType,
      }: OpenUrlOptions = {},
    ): void => {
      let isEmbedded = true;
      try {
        isEmbedded = window.self !== window.top;
      } catch {
        isEmbedded = true; // cross-origin frame access throws → we ARE embedded
      }

      if (isEmbedded && sdk?.redirectToContent) {
        const command = buildWebviewCommand(url, {
          utilityButtonUuid, targetBuildingUuid, isModule, linkType,
        });

        // Stored from here rather than from the caller so what is remembered is the object that was
        // actually sent. A module is a page, not a record, and has no pane to be restored into.
        if (recordId && !isModule) rememberSelection(recordId, command);

        sdk.redirectToContent(command);
        return;
      }

      /*
       * Standalone (new tab). The launch params carry the reader's auth token, so they go only to
       * one of OUR miniapps, which needs them to bootstrap its own SDK. Since VIS-2189 this url can
       * be a building's own visitor system or any other link-out, and appending a credential to a
       * third party's url would hand it over in the query string — in their logs, their referrers,
       * their history. Those open with the url exactly as the button has it.
       */
      if (!isOurOrigin(url, ENV_URLS)) {
        window.open(url, '_blank', 'noopener');
        return;
      }

      const launchParams = new URLSearchParams({
        authToken,
        apiUrl,
        appBrand,
        buildingUuid: targetBuildingUuid ?? buildingUuid ?? '',
        locale,
      });
      const separator = url.includes('?') ? '&' : '?';
      window.open(`${url}${separator}${launchParams.toString()}`, '_blank', 'noopener');
    },
    [sdk, authToken, apiUrl, appBrand, buildingUuid, locale, rememberSelection],
  );

  /*
   * A page, not a record. The portal understands a `NATIVE_SCREEN` that names one of its stable
   * routes and carries no uuid; nothing else does, so this is gated on the portal SPECIFICALLY
   * rather than on `isDesktopHost`. The native apps are left exactly as they are — an unhandled
   * command there would be a dead button, which is worse than the destination they have today.
   */
  const canOpenHostPage = isTenantPortal;

  const openHostPage = useCallback(
    (page: HostPage): void => {
      if (!canOpenHostPage || !sdk?.redirectToContent) return;

      sdk.redirectToContent({
        value: page,
        type: LinkTypeEnum.NATIVE_SCREEN,
        options: { ...(buildingUuid ? { building_uuid: buildingUuid } : {}) },
      });
    },
    [canOpenHostPage, sdk, buildingUuid],
  );

  const openActivityItem = useCallback(
    (item: ActivityItem, action?: ActivityAction): void => {
      const url = getDeeplinkUrl(item, action);
      if (!url) return; // unroutable action degrades to a no-op; the card renders non-tappable

      /*
       * Whether the PORTAL has a route of its own for this record, which is the only reason to name
       * a button: the portal upgrades a webview request that names one into an in-app route, and a
       * vertical with no route for the record would land on its list instead of the record.
       *
       * Each arm is gated on the url actually carrying what that route needs, not merely on the
       * type — a request the portal would decline is better left unnamed, because then the miniapp
       * opens the record itself, which is the better of the two outcomes.
       *
       * - A VISIT: the visitor MFE has no per-visit route but it does have a DAY, and the url names
       *   it. Without the day the portal falls back to the vertical home.
       * - A BOOKING: `bookings/:transactionUuid` is a real portal route (RES-1367). The url this
       *   names is the same one the phone shells open directly — the portal just has a better place
       *   to put it than an iframe of the miniapp.
       */
      const portalHasARouteForTheRecord = (item.type === 'visitor_registration' && url.includes('visitDate='))
        || (item.type === 'resource_booking' && url.includes('transactionUuid='));

      const itemBuildingUuid = buildingUuidFor(item);

      /*
       * The button names the vertical, not the building, and the portal is free to land the request
       * in a different building than the one named — it resolves the uuid against the buttons of
       * whichever building it actually lands on, and recovers the right button from the url when
       * that lookup misses. So this no longer asks whether the record is in the building on screen:
       * answering that question here only ever withheld a hint the portal knew how to ignore.
       *
       * It IS gated on NOT being the portal's split surface, where naming a button is the wrong request
       * entirely. There the host reads a named button as "open this whole vertical" and leaves the
       * page for it, which is right for a module CTA and wrong for a record: a visit in the
       * building on screen jumped to the visitor MFE while the same visit in ANOTHER building
       * opened in the pane, because that one could never name a button to begin with. Same record,
       * two behaviours, decided by something the reader cannot see.
       *
       * A record always goes to the pane there. This function opens records; `openModule` opens
       * modules and keeps naming its button.
       */
      const portalCanRouteTheRecord = portalHasARouteForTheRecord && !isSplitSurface;

      openUrl(url, {
        utilityButtonUuid: portalCanRouteTheRecord ? portalMfeButtonUuid(item.type) : undefined,
        targetBuildingUuid: itemBuildingUuid,
        /*
         * Only when the action opens the RECORD. `resolveTarget` sends `book_again` down its own
         * branch today, so this cannot currently be one — but this function is on the hook's public
         * interface, and remembering a creation flow as the selection would light a row up beside a
         * pane showing something else. Same rule the feed uses to move the highlight.
         */
        recordId: NON_RECORD_ACTIONS.has(action?.type ?? '') ? undefined : item.id,
      });
    },
    [getDeeplinkUrl, openUrl, portalMfeButtonUuid, buildingUuidFor, isSplitSurface],
  );

  /*
   * Base-miniapp landing for a whole vertical (empty-state CTA) — needs only the type + buildingUuid.
   *
   * VIS-2189: when the caller names the building's button and our flow cannot serve it, the CTA goes
   * where the button goes instead, so a tenant reaches the same place from here as from the home
   * screen — the building's own visitor system included.
   */
  const getModuleUrl = useCallback(
    (type: ActivityType, { target }: ModuleOptions = {}): string | null => {
      if (target?.kind === 'button') {
        return target.url;
      }

      return buildBaseDeeplink({ type, context: { buildingUuid } }, ENV_URLS, buildingUuid, 'create', {
        appInstanceUuid: target?.appInstanceUuid,
      });
    },
    [buildingUuid],
  );

  const canOpenModule = useCallback(
    (type: ActivityType, options?: ModuleOptions): boolean => getModuleUrl(type, options) !== null,
    [getModuleUrl],
  );

  const openModule = useCallback(
    (type: ActivityType, options?: ModuleOptions): void => {
      const url = getModuleUrl(type, options);
      const target = options?.target;
      const linkType = target?.kind === 'button' ? LINK_TYPE_BY_BUTTON_TYPE[target.buttonType] : undefined;
      // A module CTA asks for the vertical, which is exactly what the portal's MFE route is, so this
      // one names its button AND marks itself a module. The url stays the fallback for every other
      // host. A module CTA is always about the building the reader is on, so it carries that one.
      if (url) {
        openUrl(url, {
          utilityButtonUuid: portalMfeButtonUuid(type), targetBuildingUuid: buildingUuid, isModule: true, linkType,
        });
      }
    },
    [getModuleUrl, openUrl, portalMfeButtonUuid, buildingUuid],
  );

  /*
   * Leave behind what the split surface would have sent, without sending it.
   *
   * The popover has no pane, so a booking opens the in-app screen instead of going out to the
   * host — which meant the one record the reader can actually be sitting on there was the one
   * record nothing recorded. Expanding to full screen then restored whatever the LAST split-surface
   * visit had left, so the reader arrived on a different booking than the one they were reading.
   *
   * The command is built here rather than stored from a send, so it has to be the same object
   * `openUrl` would have produced — hence the shared builder. `getDeeplinkUrl` supplies a booking's
   * receipt url, which is exactly what the pane frames on the other surface.
   */
  const rememberInAppRecord = useCallback(
    (item: ActivityItem): void => {
      /*
       * The RECORD action, not the primary one. The in-app screen this is recording picks its
       * transaction with the same lookup, and a tile whose first action is something else — "Book
       * again" — would otherwise be navigated by one action and remembered by another.
       */
      const action = getRecordAction(item);
      if (!action) return;

      const itemBuildingUuid = buildingUuidFor(item);
      /*
       * `buildActivityDeeplink` directly, NOT `getDeeplinkUrl`, and the difference is the whole
       * point: that function falls back to the module home when a record cannot be addressed, which
       * is the honest destination for a TAP and the wrong thing entirely to remember. Stored under
       * the record's id, a module url reopens the vertical's landing page with the row marked as
       * though it were the record. No record url, no selection — the pane stays on what it had.
       */
      const url = buildActivityDeeplink(item, action, itemBuildingUuid, ENV_URLS, { inPane: true });
      if (!url) return;

      rememberInAppSelection(
        item.id,
        buildWebviewCommand(url, { targetBuildingUuid: itemBuildingUuid }),
      );
    },
    [rememberInAppSelection, buildingUuidFor],
  );

  return {
    getDeeplinkUrl,
    canOpen,
    openActivityItem,
    canOpenModule,
    getModuleUrl,
    openModule,
    canOpenHostPage,
    openHostPage,
    rememberInAppRecord,
  };
};
